import { annotatorClient } from './annotatorClient';
import type { AnnotatorProject, Label } from '../types/annotator';
import { CameraUploadImage, getCameraUploadImageFileUrl } from './cameraUpload';

export async function listAnnotatorProjects(): Promise<AnnotatorProject[]> {
  const projects = await annotatorClient.get<any[]>('/projects/');
  return projects.map((p) => ({
    ...p,
    labels: (p.labels ?? []).map((l: any) => ({ ...l, color: l.color ?? l.colour ?? '#FF0000' })),
  }));
}

async function findOrCreateLabel(projectId: number, project: AnnotatorProject, className: string): Promise<Label> {
  const existing = project.labels.find((l) => l.name.toLowerCase() === className.toLowerCase());
  if (existing) return existing;

  const maxIndex = project.labels.reduce((max, l) => Math.max(max, l.class_index), -1);
  const created = await annotatorClient.post<any>(`/projects/${projectId}/labels`, {
    name: className,
    class_index: maxIndex + 1,
  });
  const label: Label = {
    id: created.id,
    name: created.name,
    color: created.color ?? created.colour ?? '#FF0000',
    class_index: created.class_index,
    annotation_count: created.annotation_count ?? 0,
  };
  project.labels.push(label);
  return label;
}

export interface PushResult {
  fileName: string;
  pushedClasses: string[];
  skippedNoBox: string[];
}

// Uploads one Camera Upload image + its boxed classifications into an
// Annotator project as training data. Classifications with no drawn/detected
// box are skipped (a bounding-box annotation can't exist without one) —
// callers should tell the admin which ones were skipped.
export async function pushImageToTraining(
  deviceId: string,
  image: CameraUploadImage,
  project: AnnotatorProject,
  accessToken: string,
): Promise<PushResult> {
  if (!image.width || !image.height) {
    throw new Error('This image has no stored dimensions (uploaded before box-drawing support) — re-upload it first.');
  }

  const boxed = image.classifications.filter((c) => c.boxX1 != null && c.boxY1 != null && c.boxX2 != null && c.boxY2 != null);
  const skippedNoBox = image.classifications.filter((c) => !boxed.includes(c)).map((c) => c.className);
  if (boxed.length === 0) {
    throw new Error('None of this image\'s classifications have a bounding box — draw one first.');
  }

  const fileRes = await fetch(getCameraUploadImageFileUrl(deviceId, image.id), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!fileRes.ok) throw new Error(`Failed to fetch original image (HTTP ${fileRes.status})`);
  const blob = await fileRes.blob();
  const file = new File([blob], image.originalFileName, { type: image.mimeType });

  const form = new FormData();
  form.append('files', file);
  const uploaded = await annotatorClient.postForm<any[]>(`/projects/${project.id}/images/upload`, form);
  const annotatorImage = uploaded[0];
  if (!annotatorImage) throw new Error('Annotator did not return an uploaded image');

  const width = image.width;
  const height = image.height;
  const annotations = [];
  for (const c of boxed) {
    const label = await findOrCreateLabel(project.id, project, c.className);
    const x1 = c.boxX1 as number, y1 = c.boxY1 as number, x2 = c.boxX2 as number, y2 = c.boxY2 as number;
    annotations.push({
      label_id: label.id,
      x_center: ((x1 + x2) / 2) / width,
      y_center: ((y1 + y2) / 2) / height,
      box_width: (x2 - x1) / width,
      box_height: (y2 - y1) / height,
    });
  }

  await annotatorClient.put(`/projects/${project.id}/images/${annotatorImage.id}/annotations/`, { annotations });

  return { fileName: image.originalFileName, pushedClasses: boxed.map((c) => c.className), skippedNoBox };
}
